import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./context/AuthContext";
import { useLogout } from "./hooks/useAuthApi";
import { useAdminUsers, useUpdateUserRole, useUpdateUserStatus, useBackendLogs, useUnlockStack } from "./hooks/useAdminApi";
import "./AdminPage.css";

export default function AdminPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const logoutMutation = useLogout();
  const { data: users, isLoading, error } = useAdminUsers();
  const updateRoleMutation = useUpdateUserRole();
  const updateStatusMutation = useUpdateUserStatus();
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [unlockStackName, setUnlockStackName] = useState("");
  const [unlockResult, setUnlockResult] = useState(null);
  const { data: logsData, refetch: refetchLogs } = useBackendLogs();
  const unlockMutation = useUnlockStack();
  const logsEndRef = useRef(null);

  useEffect(() => {
    if (showLogsModal && logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [logsData, showLogsModal]);

  const handleLogout = async () => {
    try {
      await logoutMutation.mutateAsync();
      navigate("/");
    } catch (err) {
      console.error("Logout failed:", err);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await updateRoleMutation.mutateAsync({ userId, role: newRole });
    } catch (err) {
      console.error("Failed to update role:", err);
    }
  };

  const handleStatusToggle = async (userId, currentStatus) => {
    const newStatus = currentStatus === "active" ? "inactive" : "active";
    try {
      await updateStatusMutation.mutateAsync({ userId, status: newStatus });
    } catch (err) {
      console.error("Failed to update status:", err);
    }
  };

  const handleUnlockStack = async () => {
    if (!unlockStackName.trim()) return;
    setUnlockResult(null);
    try {
      const data = await unlockMutation.mutateAsync({ stackName: unlockStackName.trim() });
      setUnlockResult({ success: true, message: data.message });
      setUnlockStackName("");
    } catch (err) {
      setUnlockResult({ success: false, message: err.message });
    }
  };

  const handleCloseUnlockModal = () => {
    setShowUnlockModal(false);
    setUnlockStackName("");
    setUnlockResult(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "Never";
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className="admin-page">
      <div className="admin-container">
        <div className="header">
          <div className="header-left">
            <img src="/Phaseshift_Favicon-01.png" alt="" className="header-logo" />
            <h1>User Management</h1>
            {user && <span className="user-info">{user.firstName || user.email}</span>}
          </div>
          <div className="header-actions">
            <button className="logs-btn" onClick={() => setShowLogsModal(true)}>
              Logs
            </button>
            <button className="unlock-btn" onClick={() => setShowUnlockModal(true)}>
              Unlock
            </button>
            <button className="nav-btn" onClick={() => navigate("/dashboard")}>
              ← Dashboard
            </button>
            <button className="logout-btn" onClick={handleLogout}>
              Logout
            </button>
          </div>
        </div>

        {isLoading && (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading users...</p>
          </div>
        )}

        {error && (
          <div className="error-state">
            <p>Failed to load users: {error.message}</p>
          </div>
        )}

        {!isLoading && !error && users && (
          <>
            <div className="users-table-container desktop-only">
              <table className="users-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Status</th>
                    <th>Role</th>
                    <th>Instances</th>
                    <th>Last Login</th>
                    <th>Last Deploy</th>
                    <th>Joined</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id}>
                      <td>
                        {u.firstName && u.lastName
                          ? `${u.firstName} ${u.lastName}`
                          : u.firstName || "—"}
                      </td>
                      <td>{u.email}</td>
                      <td>
                        <span className={`status-badge status-${u.status}`}>
                          {u.status === "active" ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td>
                        <span className={`role-badge role-${u.role.toLowerCase()}`}>
                          {u.role}
                        </span>
                      </td>
                      <td>
                        <span className="instance-count">{u.instanceCount}</span>
                      </td>
                      <td className="date-cell">
                        <span className={u.lastLoginAt ? "activity-recent" : "activity-never"}>
                          {formatDateTime(u.lastLoginAt)}
                        </span>
                      </td>
                      <td className="date-cell">
                        <span className={u.lastDeploymentAt ? "activity-recent" : "activity-never"}>
                          {formatDateTime(u.lastDeploymentAt)}
                        </span>
                      </td>
                      <td className="date-cell">{formatDate(u.createdAt)}</td>
                      <td className="actions-cell">
                        {u.id !== user?.userId ? (
                          <>
                            <select
                              value={u.role}
                              onChange={(e) => handleRoleChange(u.id, e.target.value)}
                              disabled={updateRoleMutation.isPending}
                              className="role-select"
                            >
                              <option value="USER">USER</option>
                              <option value="ADMIN">ADMIN</option>
                            </select>
                            <button
                              className={`status-toggle-btn ${u.status === "active" ? "deactivate" : "activate"}`}
                              onClick={() => handleStatusToggle(u.id, u.status)}
                              disabled={updateStatusMutation.isPending}
                            >
                              {u.status === "active" ? "Deactivate" : "Activate"}
                            </button>
                          </>
                        ) : (
                          <span className="self-badge">You</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="users-cards-container mobile-only">
              {users.map((u) => (
                <div key={u.id} className="user-card">
                  <div className="user-card-row user-card-main">
                    <div className="user-card-info">
                      <span className="user-card-name">
                        {u.firstName && u.lastName
                          ? `${u.firstName} ${u.lastName}`
                          : u.firstName || "—"}
                      </span>
                      <span className="user-card-email">{u.email}</span>
                    </div>
                    <div className="user-card-badges">
                      <span className={`status-badge status-${u.status}`}>
                        {u.status === "active" ? "Active" : "Inactive"}
                      </span>
                      <span className={`role-badge role-${u.role.toLowerCase()}`}>
                        {u.role}
                      </span>
                      <span className="instance-count">{u.instanceCount}</span>
                    </div>
                  </div>
                  <div className="user-card-row user-card-dates">
                    <div className="user-card-date">
                      <span className="date-label">Login</span>
                      <span className={u.lastLoginAt ? "activity-recent" : "activity-never"}>
                        {formatDateTime(u.lastLoginAt)}
                      </span>
                    </div>
                    <div className="user-card-date">
                      <span className="date-label">Deploy</span>
                      <span className={u.lastDeploymentAt ? "activity-recent" : "activity-never"}>
                        {formatDateTime(u.lastDeploymentAt)}
                      </span>
                    </div>
                    <div className="user-card-date">
                      <span className="date-label">Joined</span>
                      <span>{formatDate(u.createdAt)}</span>
                    </div>
                  </div>
                  <div className="user-card-row user-card-actions">
                    {u.id !== user?.userId ? (
                      <>
                        <select
                          value={u.role}
                          onChange={(e) => handleRoleChange(u.id, e.target.value)}
                          disabled={updateRoleMutation.isPending}
                          className="role-select"
                        >
                          <option value="USER">USER</option>
                          <option value="ADMIN">ADMIN</option>
                        </select>
                        <button
                          className={`status-toggle-btn ${u.status === "active" ? "deactivate" : "activate"}`}
                          onClick={() => handleStatusToggle(u.id, u.status)}
                          disabled={updateStatusMutation.isPending}
                        >
                          {u.status === "active" ? "Deactivate" : "Activate"}
                        </button>
                      </>
                    ) : (
                      <span className="self-badge">You</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {showUnlockModal && (
        <div className="modal-overlay" onClick={handleCloseUnlockModal}>
          <div className="unlock-modal" onClick={(e) => e.stopPropagation()}>
            <div className="unlock-modal-header">
              <h2>Unlock Stack</h2>
              <button className="close-modal-btn" onClick={handleCloseUnlockModal}>
                ✕
              </button>
            </div>
            <div className="unlock-modal-body">
              <p className="unlock-description">
                Cancel a stuck Pulumi deployment by entering the stack name below.
              </p>
              <label htmlFor="stack-name">Stack Name</label>
              <input
                id="stack-name"
                type="text"
                value={unlockStackName}
                onChange={(e) => setUnlockStackName(e.target.value)}
                placeholder="e.g. demo-1770389100252"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter" && unlockStackName.trim()) {
                    handleUnlockStack();
                  }
                }}
              />
              {unlockResult && (
                <div className={`unlock-result ${unlockResult.success ? "success" : "error"}`}>
                  {unlockResult.message}
                </div>
              )}
            </div>
            <div className="unlock-modal-footer">
              <button className="cancel-btn" onClick={handleCloseUnlockModal}>
                Cancel
              </button>
              <button
                className="confirm-unlock-btn"
                onClick={handleUnlockStack}
                disabled={!unlockStackName.trim() || unlockMutation.isPending}
              >
                {unlockMutation.isPending ? "Unlocking..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showLogsModal && (
        <div className="modal-overlay" onClick={() => setShowLogsModal(false)}>
          <div className="logs-modal" onClick={(e) => e.stopPropagation()}>
            <div className="logs-modal-header">
              <h2>Backend Logs</h2>
              <div className="logs-modal-actions">
                <button className="refresh-logs-btn" onClick={() => refetchLogs()}>
                  ↻ Refresh
                </button>
                <button className="close-modal-btn" onClick={() => setShowLogsModal(false)}>
                  ✕
                </button>
              </div>
            </div>
            <div className="logs-content">
              <pre>{logsData?.logs || "No logs available"}</pre>
              <div ref={logsEndRef} />
            </div>
            <div className="logs-modal-footer">
              <span className="log-count">{logsData?.count || 0} lines (auto-refreshes every 5s)</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
